import React, { useEffect, useState as useState$1, useRef, useCallback, useMemo, useLayoutEffect, useReducer } from 'react';

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Comme `useEffect` mais le hook n'est exécuter que si les conditions correspondent au prédicat.
 *
 * @param callback Callback passé au hook.
 * @param deps Liste des dépendances comme pour `useEffect`. S'il n'est pas undefined,
 * l'effet sera déclenché lorsque les dépendances ont changé ET que les conditions satisfont le prédicat.
 * @param conditions Tableau de conditions.
 * @param predicate Prédicat qui définit si les conditions sont toutes vrai.
 * @param effectHook Hook qui sera utilisé pour exécuter le callback.
 * Doit être conforme à la signature `useEffect`, ce qui signifie que le callback doit être placé comme premier argument
 * et la liste des dépendances comme second.
 * @param effectHookRestArgs Arguments supplémentaires qui sont passés à `effectHook`.
 */
function useConditionalEffect(callback, deps, conditions, predicate = truthyAndArrayPredicate, effectHook = useEffect, ...effectHookRestArgs) {
    effectHook(
    // eslint-disable-next-line consistent-return
    (() => {
        if (predicate(conditions)) {
            return callback();
        }
    }), deps, ...effectHookRestArgs);
}
const truthyAndArrayPredicate = (conditions) => conditions.every((i) => Boolean(i));

function useControlledState(value, defaultValue, onChange) {
    if (typeof value === "function") {
        throw new TypeError("Les fonctions ne sont pas supportées en tant que valeurs.");
    }
    const [stateValue, setStateValue] = useState$1(value || defaultValue);
    const ref = useRef(value !== undefined);
    const isControlled = value !== undefined;
    const stateRef = useRef(stateValue);
    ref.current = isControlled;
    const setValue = useCallback((value, ...args) => {
        const onChangeCaller = (value, ...onChangeArgs) => {
            if (onChange) {
                if (stateRef.current !== value) {
                    if (typeof value === "function")
                        onChange(value(stateRef.current), ...onChangeArgs);
                    else
                        onChange(value, ...onChangeArgs);
                }
            }
            if (!isControlled) {
                stateRef.current = value;
            }
        };
        if (!isControlled) {
            setStateValue(value);
        }
        onChangeCaller(value, ...args);
    }, [isControlled, onChange]);
    if (isControlled) {
        stateRef.current = value;
    }
    else {
        value = stateValue;
    }
    return [value, setValue];
}

/**
 * Exécute un effect lors du unmount d'un composant
 */
function useUnmountEffect(effect) {
    useEffect(() => () => {
        effect();
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
}

/**
 * Comme `useCallback` mais la fonction est debounced
 *
 * @param callback
 * @param deps Liste des dépendances de `useCallback`
 * @param delay Délai.
 * @param maxWait Le temps maximum que `callback` est autorisé à être retardé avant d'être invoquée. 0 signifie pas d'attente maximale.
 */
function useDebouncedCallback(callback, deps, delay, maxWait = 0) {
    const timeout = useRef();
    const waitTimeout = useRef();
    const lastCall = useRef();
    const clear = () => {
        if (timeout.current) {
            clearTimeout(timeout.current);
            timeout.current = undefined;
        }
        if (waitTimeout.current) {
            clearTimeout(waitTimeout.current);
            waitTimeout.current = undefined;
        }
    };
    // Annule l'exécution planifiée au unmount
    useUnmountEffect(clear);
    return useMemo(() => {
        const execute = () => {
            if (!lastCall.current)
                return;
            const context = lastCall.current;
            lastCall.current = undefined;
            callback.apply(context.this, context.args);
            clear();
        };
        const wrapped = function (...args) {
            if (timeout.current) {
                clearTimeout(timeout.current);
            }
            lastCall.current = { args, this: this };
            // planifie l'exécution
            timeout.current = setTimeout(execute, delay);
            // planifie l'exécution avec un délai maximum
            if (maxWait > 0 && !waitTimeout.current) {
                waitTimeout.current = setTimeout(execute, maxWait);
            }
        };
        Object.defineProperties(wrapped, {
            length: { value: callback.length },
            name: { value: `${callback.name || "anonymous"}__debounced__${delay}` }
        });
        return wrapped;
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [delay, maxWait, ...deps]);
}

/**
 * Comme `useEffect` mais debounced
 *
 * @param callback Callback comme `useEffect`, mais sans la posibilité de retouner une fonction de cleanup.
 * @param deps Liste des dépendances.
 * @param delay Délai.
 * @param maxWait Temps maximum autorisé à être retardé. 0 signifie pas d'attente maximale.
 */
function useDebouncedEffect(callback, deps, delay, maxWait = 0) {
    useEffect(useDebouncedCallback(callback, deps, delay, maxWait), deps);
}

/**
 * Retourne une fonction qui donne si le composant est monté.
 *
 */
function useIsMounted() {
    const isMounted = useRef(false);
    const get = useCallback(() => isMounted.current, []);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);
    return get;
}

/**
 * Comme `useState` mais est protégé sur les composants non montés.
 */
function useSafeState(initialState) {
    const [state, setState] = useState$1(initialState);
    const isMounted = useIsMounted();
    return [
        state,
        useCallback((value) => {
            if (isMounted())
                setState(value);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [])
    ];
}

/**
 * Comme `useSafeState` mais debounced.
 *
 * @param initialState Initial state to pass to underlying `useSafeState`.
 * @param delay Délai.
 * @param maxWait Temps maximum autorisé à être retardé. 0 signifie pas d'attente maximale.
 */
function useDebouncedState(initialState, delay, maxWait = 0) {
    const [state, setState] = useSafeState(initialState);
    return [state, useDebouncedCallback(setState, [], delay, maxWait)];
}

// eslint-disable-next-line @typescript-eslint/ban-types
function useTimeout(fn, delay) {
    const timeout = useRef();
    const callback = useRef(fn);
    const set = useCallback(() => {
        timeout.current && clearTimeout(timeout.current);
        timeout.current = setTimeout(() => {
            callback.current();
        }, delay);
    }, [delay]);
    const clear = useCallback(() => {
        timeout.current && clearTimeout(timeout.current);
    }, []);
    useEffect(() => {
        callback.current = fn;
    }, [fn]);
    useEffect(() => {
        set();
        return clear;
    }, [delay, clear, set]);
    return { clear, reset: set };
}

/**
 * @deprecated Utiliser `useDebouncedEffect` à la place
 */
function useDebounce(fn, ms = 50, deps = []) {
    const { clear, reset } = useTimeout(fn, ms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(reset, deps);
    return clear;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * @deprecated utiliser `useDebouncedCallback` à la place
 */
function useDebounceFn(fn, waitMilliseconds = 50) {
    const fnRef = useRef(fn);
    fnRef.current = fn;
    const debounced = useCreation(() => debounce(((...args) => {
        return fnRef.current(...args);
    }), waitMilliseconds), []);
    return debounced;
}
function useCreation(factory, deps) {
    const { current } = useRef({
        deps,
        obj: undefined,
        initialized: false
    });
    if (current.initialized === false || !depsAreSame(current.deps, deps)) {
        current.deps = deps;
        current.obj = factory();
        current.initialized = true;
    }
    return current.obj;
}
function depsAreSame(oldDeps, deps) {
    if (oldDeps === deps)
        return true;
    for (const i in oldDeps) {
        if (oldDeps[i] !== deps[i])
            return false;
    }
    return true;
}
function debounce(func, waitMilliseconds = 50) {
    let timeoutId;
    return function (...args) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const context = this;
        const doLater = function () {
            timeoutId = undefined;
            func.apply(context, args);
        };
        if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(doLater, waitMilliseconds);
    };
}

/**
 * @deprecated Utiliser `useDebouncedState` à la place.
 */
function useDebounceValue(value, waitMilliseconds = 250) {
    const [debounced, setDebounced] = useState$1(value);
    const run = useDebounceFn(() => {
        setDebounced(value);
    }, waitMilliseconds);
    useEffect(() => {
        run();
    }, [run, value]);
    return debounced;
}

function getDimensionObject(node) {
    const rect = node.getBoundingClientRect();
    return {
        width: rect.width,
        height: rect.height,
        top: rect.top,
        left: rect.left,
        x: rect.x,
        y: rect.y,
        right: rect.right,
        bottom: rect.bottom
    };
}
/**
 * Retourne une ref à placer sur l'élément que l'on veut mesurer à chaque resize/scroll de la fenêtre et les dimensions de l'élément
 */
function useDimensions() {
    const [dimensions, setDimensions] = useState$1({
        bottom: 0,
        left: 0,
        right: 0,
        top: 0,
        x: 0,
        y: 0,
        height: 0,
        width: 0
    });
    const ref = useRef();
    useLayoutEffect(() => {
        if (ref.current) {
            const measure = () => window.requestAnimationFrame(() => setDimensions(getDimensionObject(ref.current)));
            measure();
            window.addEventListener("resize", measure);
            window.addEventListener("scroll", measure);
            return () => {
                window.removeEventListener("resize", measure);
                window.removeEventListener("scroll", measure);
            };
        }
    }, []);
    return [ref, dimensions];
}

/**
 * Retourne true si on est dans le premier render
 */
function useFirstMountState() {
    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = false;
    }, []);
    return isMounted.current;
}

const stateChanger = (state) => !state;
/**
 * Retourne une fonction qui force un re-render du composant
 */
function useForceUpdate() {
    const [, setState] = useSafeState(false);
    return useCallback(() => {
        setState(stateChanger);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

const isBrowser$4 = typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof document !== "undefined";
/**
 * Quand utiliser ce hook ? À chaque fois qu'on utilise un useLayoutEffect qui peut potentiellement se passer en SSR
 * (useLayoutEffect pas disponible en SSR)
 */
const useIsomorphicEffect = isBrowser$4 ? useLayoutEffect : useEffect;

var _a;
let serverHandoffComplete = false;
let id = 0;
const genId = () => ++id;
/**
 * @deprecated utiliser `useId` de react 18 à la place
 * Auto-génère des ID pour l'accessibilité et le SSR
 *
 * https://github.com/reach/reach-ui/blob/develop/packages/auto-id/src/index.tsx
 */
let useId = (_a = React.useId) !== null && _a !== void 0 ? _a : function useId() {
    const initialId = serverHandoffComplete ? genId() : null;
    const [id, setId] = useState$1(initialId);
    useIsomorphicEffect(() => {
        if (id === null) {
            setId(genId());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (serverHandoffComplete === false) {
            serverHandoffComplete = true;
        }
    }, []);
    return id != null ? String(id) : undefined;
};

/**
 * @param element Détecte si il y a une intéraction en déhors de l'élement
 * @param onInteractOutside Fonction appelée quand il y a une intéraction en dehors
 */
function useInteractOutside(element, onInteractOutside) {
    const stateRef = useRef({
        isPointerDown: false
    });
    const state = stateRef.current;
    useEffect(() => {
        const onPointerDown = (e) => {
            if (isValidEvent(e, element)) {
                state.isPointerDown = true;
                if (e.button === 0) {
                    e.preventDefault();
                }
            }
        };
        const onPointerUp = (e) => {
            if (state.isPointerDown && onInteractOutside && isValidEvent(e, element)) {
                state.isPointerDown = false;
                onInteractOutside(e);
                if (e.button === 0) {
                    e.preventDefault();
                }
            }
        };
        document.addEventListener("pointerdown", onPointerDown, true);
        document.addEventListener("pointerup", onPointerUp, true);
        return () => {
            document.removeEventListener("pointerdown", onPointerDown, true);
            document.removeEventListener("pointerup", onPointerUp, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onInteractOutside, element, state.isPointerDown]);
}
function isValidEvent(e, element) {
    if (e.button > 0) {
        return false;
    }
    if (e.target) {
        const ownerDocument = e.target.ownerDocument;
        if (!ownerDocument) {
            return false;
        }
    }
    return element && !element.contains(e.target);
}

/**
 * Exécute un effect lors du mount d'un composant
 */
function useMountEffect(effect) {
    useEffect(() => {
        effect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

/**
 * Retourne la valeur passée au hook lors du render précédent.
 * @param value Valeur à garder en mémoire
 */
function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}

/**
 * Comme `useRef`, mais retourne une ref immuable qui contient la valeur réelle.
 */
function useSyncedRef(value) {
    const _ref = useRef(value);
    _ref.current = value;
    return useMemo(() => Object.freeze({
        get current() {
            return _ref.current;
        }
    }), []);
}

const noop$2 = () => undefined;
/**
 * `useEffect` qui ignore le premier render.
 *
 * @param effect Effet à exécuter sur chaque update
 * @param deps Liste de dépendances de l'effet, comme `useEffect`
 */
function useUpdateEffect(effect, deps) {
    const isFirstRender = useFirstMountState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(isFirstRender ? noop$2 : effect, deps);
}

const isBrowser$3 = typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof document !== "undefined";
function resolveHookState(nextState, prevState) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    if (typeof nextState === "function")
        return nextState(prevState);
    return nextState;
}
/**
 * @param storage Instance de Storage qui sera gérée
 * @param key
 * @param defaultValue
 * @param options
 */
function useStorageValue(storage, key, defaultValue = null, options = {}) {
    const { isolated } = options;
    let { initializeWithStorageValue = true, handleStorageEvent = true, storeDefaultValue = false } = options;
    // Évite de récupérer la valeur pendant la phase SSR
    if (!isBrowser$3) {
        storeDefaultValue = false;
        initializeWithStorageValue = false;
        handleStorageEvent = false;
    }
    const methods = useSyncedRef({
        fetchVal: () => parse(storage.getItem(key), defaultValue),
        storeVal: (val) => {
            const stringified = stringify(val);
            if (stringified) {
                storage.setItem(key, stringified);
                return true;
            }
            return false;
        },
        removeVal: () => {
            storage.removeItem(key);
        },
        setVal: (val) => {
            setState(parse(val, defaultValue));
        },
        fetchState: () => {
            const newVal = methods.current.fetchVal();
            setState(newVal);
            return newVal !== stateRef.current ? newVal : null;
        },
        setState: (nextState) => {
            setState(nextState === null ? defaultValue : nextState);
        }
    });
    const isFirstMount = useFirstMountState();
    const [state, setState] = useSafeState(initializeWithStorageValue && isFirstMount ? methods.current.fetchVal() : undefined);
    const prevState = usePrevious(state);
    const stateRef = useSyncedRef(state);
    const keyRef = useSyncedRef(key);
    const isolatedRef = useSyncedRef(isolated);
    // Récupére la valeur au mount du hook pour le cas où `initializeWithStorageValue` est à false
    useMountEffect(() => {
        if (!initializeWithStorageValue) {
            methods.current.fetchState();
        }
    });
    // Stocke la valeur par défaut si elle n'est pas null et si les options sont configurées pour stocker la valeur par défaut
    useConditionalEffect(() => {
        methods.current.storeVal(defaultValue);
    }, undefined, [prevState !== state, storeDefaultValue && state === defaultValue && defaultValue !== null]);
    // Récupère la valeur lorsque la clé change
    useUpdateEffect(() => {
        methods.current.fetchState();
    }, [key]);
    useIsomorphicEffect(() => {
        if (!handleStorageEvent)
            return;
        const storageHandler = (ev) => {
            if (ev.storageArea !== storage)
                return;
            if (ev.key !== keyRef.current)
                return;
            methods.current.setVal(ev.newValue);
        };
        window.addEventListener("storage", storageHandler, { passive: true });
        return () => {
            window.removeEventListener("storage", storageHandler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleStorageEvent]);
    // register hook pour la synchronisation sur la même page
    useIsomorphicEffect(() => {
        if (isolated)
            return;
        let storageKeys = storageKeysUsed.get(storage);
        if (!storageKeys) {
            storageKeys = new Map();
            storageKeysUsed.set(storage, storageKeys);
        }
        let keySetters = storageKeys.get(key);
        if (!keySetters) {
            keySetters = new Set();
            storageKeys.set(key, keySetters);
        }
        const mSetState = methods.current.setState;
        keySetters.add(mSetState);
        return () => {
            keySetters === null || keySetters === void 0 ? void 0 : keySetters.delete(mSetState);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isolated, key]);
    return [
        state,
        useCallback((newState) => {
            var _a, _b;
            if (!isBrowser$3)
                return;
            const s = resolveHookState(newState, stateRef.current);
            if (methods.current.storeVal(s)) {
                methods.current.setState(s);
                if (!isolatedRef.current) {
                    // update toutes les autres instances du hook
                    (_b = (_a = storageKeysUsed
                        .get(storage)) === null || _a === void 0 ? void 0 : _a.get(keyRef.current)) === null || _b === void 0 ? void 0 : _b.forEach((setter) => {
                        if (setter === methods.current.setState)
                            return;
                        setter(s);
                    });
                }
            }
        }, 
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []),
        useCallback(() => {
            var _a, _b;
            if (!isBrowser$3)
                return;
            methods.current.removeVal();
            methods.current.setState(null);
            if (!isolatedRef.current) {
                // update toutes les autres instances du hook
                (_b = (_a = storageKeysUsed
                    .get(storage)) === null || _a === void 0 ? void 0 : _a.get(keyRef.current)) === null || _b === void 0 ? void 0 : _b.forEach((setter) => {
                    if (setter === methods.current.setState)
                        return;
                    setter(null);
                });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []),
        useCallback(() => {
            var _a, _b;
            if (!isBrowser$3)
                return;
            const newVal = methods.current.fetchState();
            if (newVal !== null && !isolatedRef.current) {
                // update toutes les autres instances du hook
                (_b = (_a = storageKeysUsed
                    .get(storage)) === null || _a === void 0 ? void 0 : _a.get(keyRef.current)) === null || _b === void 0 ? void 0 : _b.forEach((setter) => {
                    if (setter === methods.current.setState)
                        return;
                    setter(newVal);
                });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [])
    ];
}
const storageKeysUsed = new Map();
const stringify = (data) => {
    if (data === null) {
        console.warn(`'null' n'est pas une valeur valide pour le hook useStorageValue, cette opération n'aura aucun effet.`);
        return null;
    }
    try {
        return JSON.stringify(data);
    }
    catch (error) {
        console.warn(error);
        return null;
    }
};
const parse = (str, fallback) => {
    if (str === null)
        return fallback;
    try {
        return JSON.parse(str);
    }
    catch (error) {
        console.warn(error);
        return fallback;
    }
};

const isBrowser$2 = typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof document !== "undefined";
const noop$1 = () => undefined;
let IS_LOCAL_STORAGE_AVAILABLE = false;
try {
    IS_LOCAL_STORAGE_AVAILABLE = isBrowser$2 && !!window.localStorage;
}
catch (_a) {
    IS_LOCAL_STORAGE_AVAILABLE = false;
}
/**
 *
 * @param key Clé de la valeur stockée dans `localStorage`
 * @param defaultValue
 * @param options
 */
const useLocalStorageValue = IS_LOCAL_STORAGE_AVAILABLE
    ? (key, defaultValue = null, options = {}) => useStorageValue(localStorage, key, defaultValue, options)
    : (key, defaultValue = null, options = {}) => {
        if (isBrowser$2 && process.env.NODE_ENV === "development") {
            console.warn("LocalStorage n'est pas disponible dans cet environnement");
        }
        return [undefined, noop$1, noop$1, noop$1];
    };

/**
 * Fait en sorte que la fonction passée soit appelée dans la prochaine requestAnimationFrame.
 *
 * Les appels consécutifs annulent l'appel précédent.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useRafCallback(cb) {
    const cbRef = useSyncedRef(cb);
    const frame = useRef(0);
    const cancel = useCallback(() => {
        if (typeof window === "undefined")
            return;
        if (frame.current) {
            cancelAnimationFrame(frame.current);
            frame.current = 0;
        }
    }, []);
    useUnmountEffect(cancel);
    return [
        useMemo(() => {
            const wrapped = (...args) => {
                if (typeof window === "undefined")
                    return;
                cancel();
                frame.current = requestAnimationFrame(() => {
                    cbRef.current(...args);
                    frame.current = 0;
                });
            };
            Object.defineProperties(wrapped, {
                length: { value: cb.length },
                name: { value: `${cb.name || "anonymous"}__raf` }
            });
            return wrapped;
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []),
        cancel
    ];
}

let observerSingleton;
function getResizeObserver() {
    if (typeof window === "undefined")
        return undefined;
    if (observerSingleton)
        return observerSingleton;
    const callbacks = new Map();
    const observer = new ResizeObserver((entries) => {
        entries.forEach((entry) => { var _a; return (_a = callbacks.get(entry.target)) === null || _a === void 0 ? void 0 : _a.forEach((cb) => setTimeout(() => cb(entry), 0)); });
    });
    observerSingleton = {
        observer,
        subscribe: (target, callback) => {
            let cbs = callbacks.get(target);
            if (!cbs) {
                // si la cible n'a pas encore d'observer on la register
                cbs = new Set();
                callbacks.set(target, cbs);
                observer.observe(target);
            }
            cbs.add(callback);
        },
        unsubscribe: (target, callback) => {
            const cbs = callbacks.get(target);
            if (cbs) {
                cbs.delete(callback);
                if (!cbs.size) {
                    // si aucun observateur unregister complètement la cible
                    callbacks.delete(target);
                    observer.unobserve(target);
                }
            }
        }
    };
    return observerSingleton;
}
/**
 * Appelle un callback chaque fois que ResizeObserver détecte une modification de la taille de la cible.
 *
 * @param target Ref React ou Element à observer.
 * @param callback Callback appelé quand la taille change.
 */
function useResizeObserver(target, callback) {
    const ro = getResizeObserver();
    const cb = useSyncedRef(callback);
    useEffect(() => {
        if (!ro)
            return;
        let subscribed = true;
        const tgt = target && "current" in target ? target.current : target;
        if (!tgt)
            return;
        const handler = (...args) => {
            if (subscribed) {
                cb.current(...args);
            }
        };
        ro.subscribe(tgt, handler);
        return () => {
            subscribed = false;
            ro.unsubscribe(tgt, handler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target, ro]);
}

/**
 * Utilise `ResizeObserver` pour observer les dimensions d'un élément et rerender le composant lorsqu'elles changent.
 */
function useMeasure() {
    const elementRef = useRef(null);
    const [rect, setRect] = useSafeState();
    const [observerHandler] = useRafCallback((entry) => setRect(entry.contentRect));
    useResizeObserver(elementRef, observerHandler);
    return [rect, elementRef];
}

const isBrowser$1 = typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof document !== "undefined";
const queriesMap = new Map();
const querySubscribe = (query, setState) => {
    let entry = queriesMap.get(query);
    if (!entry) {
        const mql = matchMedia(query);
        const dispatchers = new Set();
        const listener = () => {
            dispatchers.forEach((d) => d(mql.matches));
        };
        if (mql.addEventListener)
            mql.addEventListener("change", listener, { passive: true });
        else
            mql.addListener(listener);
        entry = {
            mql,
            dispatchers,
            listener
        };
        queriesMap.set(query, entry);
    }
    entry.dispatchers.add(setState);
    setState(entry.mql.matches, true);
};
const queryUnsubscribe = (query, setState) => {
    const entry = queriesMap.get(query);
    if (entry) {
        const { mql, dispatchers, listener } = entry;
        dispatchers.delete(setState);
        if (!dispatchers.size) {
            queriesMap.delete(query);
            if (mql.removeEventListener)
                mql.removeEventListener("change", listener);
            else
                mql.removeListener(listener);
        }
    }
};
/**
 * Observe l'état d'une media query CSS
 *
 * Renvoi `false` dans les environnements SSR
 *
 * @param query media query CSS.
 * @param matchOnMount si l'état du hook doit être récupéré pendant un `useEffect` au lieu de la récupération synchrone.
 * Mettez ce paramètre à `true` pour les cas d'utilisation de SSR.
 */
function useMediaQuery(query, matchOnMount) {
    const rerender = useForceUpdate();
    const previousQuery = usePrevious(query);
    const state = useRef();
    const setState = useCallback((matches, initial) => {
        if (state.current !== matches) {
            state.current = matches;
            if (!initial || matchOnMount)
                rerender();
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matchOnMount]);
    if (!matchOnMount && isBrowser$1 && previousQuery !== query) {
        querySubscribe(query, setState);
    }
    useEffect(() => {
        if (matchOnMount) {
            querySubscribe(query, setState);
        }
        return () => queryUnsubscribe(query, setState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);
    return state.current;
}

function usePreventScroll(isActive) {
    useEffect(() => {
        const { paddingRight, overflow } = document.body.style;
        if (isActive) {
            document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`;
            document.body.style.overflow = "hidden";
        }
        return () => {
            document.body.style.overflow = overflow;
            document.body.style.paddingRight = paddingRight;
        };
    }, [isActive]);
}

const isBrowser = typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof document !== "undefined";
const noop = () => undefined;
let IS_SESSION_STORAGE_AVAILABLE = false;
try {
    IS_SESSION_STORAGE_AVAILABLE = isBrowser && !!window.sessionStorage;
}
catch (_a) {
    IS_SESSION_STORAGE_AVAILABLE = false;
}
/**
 *
 * @param key Clé de la valeur stockée dans `sessionStorage`
 * @param defaultValue
 * @param options
 */
const useSessionStorageValue = IS_SESSION_STORAGE_AVAILABLE
    ? (key, defaultValue = null, options = {}) => useStorageValue(sessionStorage, key, defaultValue, options)
    : (key, defaultValue = null, options = {}) => {
        if (isBrowser && process.env.NODE_ENV === "development") {
            console.warn("SessionStorage n'est pas disponible dans cet environnement");
        }
        return [undefined, noop, noop, noop];
    };

/**
 * Même utilisation que le setState dans un composant class
 *
 * Usage:
 * ```jsx
 * const [state, setState] = useState({
 *  currentIndex: 0,
 *  isPlaying: false
 * })
 *
 * setState({ isPlaying: true })
 * ```
 */
function useState(initialState) {
    return useReducer((prevState, newState) => (Object.assign(Object.assign({}, prevState), newState)), initialState);
}

function useWindowEvent(event, listener, options) {
    useEffect(() => {
        window.addEventListener(event, listener, options);
        return () => window.removeEventListener(event, listener, options);
    }, [event, listener, options]);
}

export { useConditionalEffect, useControlledState, useDebounce, useDebounceFn, useDebounceValue, useDebouncedCallback, useDebouncedEffect, useDebouncedState, useDimensions, useFirstMountState, useForceUpdate, useId, useInteractOutside, useIsMounted, useIsomorphicEffect, useLocalStorageValue, useMeasure, useMediaQuery, useMountEffect, usePreventScroll, usePrevious, useRafCallback, useResizeObserver, useSafeState, useSessionStorageValue, useState, useSyncedRef, useTimeout, useUnmountEffect, useUpdateEffect, useWindowEvent };
//# sourceMappingURL=index.js.map
